import type { IconProps } from '@shared/icons/types';
import { twMerge } from '@shared/themes';
import type { ComponentProps, ReactElement } from 'react';

type ItemPodProps = ComponentProps<'div'> & {
  text: string;
  icon: ReactElement<IconProps>;
  textClassName: string;
  /** When true, text wraps across lines instead of a single line with an ellipsis. Omit `truncate` from `textClassName` when using this. */
  allowTextWrap?: boolean;
};

/**
 * An item pod component that displays an icon and text in the same row.
 * By default the text element only receives `textClassName` (often including `truncate` for an ellipsis).
 * Set `allowTextWrap` to let long copy wrap within the row using `min-w-0`, `flex-1`, and `break-words`.
 *
 * @param text - The item pod text to display
 * @param icon - The icon component to display
 * @param className - Additional CSS classes for the container
 * @param textClassName - Additional CSS classes for the text element
 * @param allowTextWrap - When true, text wraps to multiple lines instead of truncating
 */
function ItemPod({ text, icon, className, textClassName, allowTextWrap = false, ...props }: ItemPodProps) {
  const mergedTextClassName = allowTextWrap ? twMerge('min-w-0 flex-1 break-words', textClassName) : textClassName;

  return (
    <div className={twMerge('flex items-center gap-3 p-3 rounded-lg bg-container', className)} data-testid="item-pod" data-track-name={text} {...props}>
      <div className="flex-shrink-0" data-testid="item-pod-icon">
        {icon}
      </div>
      <p className={mergedTextClassName} data-testid="item-pod-text">
        {text}
      </p>
    </div>
  );
}

export { ItemPod, type ItemPodProps };
